import { ForwardedRef, forwardRef, MouseEvent, useState } from 'react';

import { Avatar, Box, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import { getSizeProp, IconColor, IconMap, IconType } from './helpers';

const useStyles = createUseStyles<string, { block: boolean }>((theme: Theme) => ({
  icon: {
    '&.button': {
      cursor: 'pointer',
    },
    '&.white .MuiSvgIcon-root': {
      color: theme.palette.common.white,
    },
    '&.black .MuiSvgIcon-root': {
      color: theme.palette.common.black,
    },
    '&.grey .MuiSvgIcon-root': {
      color: theme.palette.gray,
    },
    '&.active .MuiSvgIcon-root': {
      color: theme.palette.secondary.main,
    },
    '&.success .MuiSvgIcon-root': {
      color: theme.palette.success.main,
    },
    '&.info .MuiSvgIcon-root': {
      color: theme.palette.chip.info,
    },
    '&.error .MuiSvgIcon-root': {
      color: theme.palette.error.main,
    },
    '&.link .MuiSvgIcon-root': {
      color: theme.palette.link.main,
    },
    '&.disabled': {
      cursor: 'default',
      opacity: 0.2,
    },
  },
  iconBackground: {
    backgroundColor: theme.palette.secondary.light,
    '&.bg-second': {
      backgroundColor: theme.palette.secondary.dark,
    },
    '&.bg-grey': {
      backgroundColor: '#00000014',
    },
    '&.small': {
      width: 24,
      height: 24,
    },
  },
  iconWrapper: {
    display: ({ block }) => (block ? 'block' : 'inline-block'),
    lineHeight: '70%',
  },
  spin: {
    animation: '$spin 400ms linear',
  },
  '@keyframes spin': {
    from: {
      transform: 'rotate(0deg)',
    },
    to: {
      transform: 'rotate(360deg)',
    },
  },
}));

interface Props {
  name: IconType;
  onClick?: (event: MouseEvent<SVGSVGElement>) => void;
  spinOnClick?: boolean;
  background?: 'first' | 'second' | 'grey';
  className?: string;
  color?: IconColor;
  size?: 'medium' | 'x-small' | 'small' | 'large' | 'x-large';
  disabled?: boolean;
  ref?: ForwardedRef<SVGSVGElement>;
  block?: boolean;
}

const Icon = forwardRef<SVGSVGElement, Props>(
  (
    {
      color = '',
      name,
      onClick,
      spinOnClick = false,
      background,
      className = '',
      size = 'medium',
      disabled = false,
      block = false,
    },
    ref
  ) => {
    const styles = useStyles({ block });
    const Icon = IconMap.get(name);
    const [spin, setSpin] = useState(false);

    if (!Icon) {
      console.warn(`unknown icon type ${name}`);
      return null;
    }

    const handleClick = (event: MouseEvent<SVGSVGElement>) => {
      if (disabled) {
        return;
      }
      onClick?.(event);
      if (spinOnClick) {
        setSpin(true);
      }
    };

    const handleStopSpin = () => {
      setSpin(false);
    };

    const canClick = Boolean(onClick);
    const role = canClick ? 'button' : 'presentation';

    const IconEl = <Icon role={role} onClick={handleClick} {...getSizeProp(size)} ref={ref} />;

    if (background) {
      const avatarClass = classNames(
        styles.icon,
        styles.iconBackground,
        className,
        `bg-${background}`,
        color,
        canClick && 'button',
        size
      );
      return <Avatar className={avatarClass}>{IconEl}</Avatar>;
    }

    const boxClass = classNames(
      styles.icon,
      styles.iconWrapper,
      spin && styles.spin,
      className,
      color,
      { disabled, button: canClick },
      size
    );
    return (
      <Box onAnimationEnd={handleStopSpin} className={boxClass}>
        {IconEl}
      </Box>
    );
  }
);

Icon.displayName = 'Icon';

export default Icon;
